<template>
  <b-table
    :fields="fields"
    :items="items"
    :busy="busy"
    :sticky-header="stickyHeader || false"
    :responsive="true"
    :bordered="bordered"
    :borderless="borderless"
    :striped="striped"
    :outlined="outlined"
    :small="small"
    :hover="hover"
    :dark="dark"
    :fixed="fixed"
    :foot-clone="footClone"
    :no-border-collapse="noCollapse"
    :empty-text="$t('general.noRecord')"
    :empty-filtered-text="$t('general.noRecord')"
    :no-local-sorting="true"
    show-empty
    class="list-table"
    :class="{
      archived: archived,
    }"
    @sort-changed="sortChanged"
  >
    <template #table-colgroup>
      <col
        v-for="field in fields"
        :key="field.key"
        :style="getColumnStyle(field)"
      >
    </template>
    <template #cell(increasement)="data">
      <b-link
        v-if="viewModal"
        class="link-underline"
        @click="view(data.item)"
      >
        {{ calculateOrderNumber(data.index) }}
      </b-link>
      <span v-else>
        {{ calculateOrderNumber(data.index) }}
      </span>
    </template>

    <template #cell(status)="data">
      <b-badge
        v-if="data.item.isEnable"
        pill
        variant="success"
        badge-glow
      >
        {{ $t(`status.active`) }}
      </b-badge>
      <b-badge
        v-else
        pill
        variant="danger"
        badge-glow
      >
        {{ $t(`status.inactive`) }}
      </b-badge>
    </template>
    <template #cell(createdAt)="data">
      {{ data.item.createdAt | formatDate }}
    </template>
    <template #cell(params)="data">
      <JsonViewer :json="data.item.params" />
    </template>
    <template #cell(updatedAt)="data">
      {{ data.item.updatedAt | formatDate }}
    </template>
    <template #cell(deletedAt)="data">
      {{ data.item.deletedAt | formatDate }}
    </template>
    <template #cell(actions)="data">
      <div class="text-nowrap">
        <slot
          name="front-button"
          :item="data.item"
        />
        <b-button
          v-if="$can('update', type) && updatable && !archived && (checkIsDefault ? !data.item.isDefault : true)"
          v-b-tooltip.hover
          :to="{
            name: `update-${route}`,
            params: { id: data.item.id },
            query: {
              departmentId: data.item.departmentId,
              assignedOperatorId: data.item.assignedOperatorId,
            }
          }"
          :title="$t('general.update')"
          variant="primary"
          pill
          size="sm"
        >
          <feather-icon icon="EditIcon" />
        </b-button>
        <b-button
          v-if="$can('delete', type) && deletable && !archived && (checkIsDefault ? !data.item.isDefault : true)"
          v-b-tooltip.hover
          :title="$t('general.delete')"
          variant="danger"
          pill
          size="sm"
          @click="destroy(data.item.id, data.index, data.item)"
        >
          <feather-icon icon="TrashIcon" />
        </b-button>
        <b-button
          v-if="$can('delete', type) && deletable && !archived && (checkIsDefault ? data.item.isDefault : false)"
          v-b-tooltip.hover
          :title="$t('alert.cannotDeleteDefaultDepartment')"
          variant="secondary"
          pill
          size="sm"
          :style="{ cursor: 'not-allowed' }"
        >
          <feather-icon icon="TrashIcon" />
        </b-button>
        <b-button
          v-if="$can('restore', type) && deletable && archived && (checkIsDefault ? !data.item.isDefault : true)"
          v-b-tooltip.hover
          :title="$t('general.restore')"
          variant="success"
          pill
          size="sm"
          @click="restore(data.item.id, data.index)"
        >
          <feather-icon icon="RepeatIcon" />
        </b-button>
        <slot
          name="back-button"
          :item="data.item"
          :index="data.index"
        />
      </div>
    </template>

    <template #table-busy>
      <div class="text-center my-1">
        <b-skeleton-table
          :rows="10"
          :columns="fields.length"
          :hide-header="true"
          :table-props="{ bordered: false, striped: true }"
        />
      </div>
    </template>

    <template #head()="scope">
      <div class="text-nowrap">
        {{ $t(scope.label) }}
      </div>
    </template>

    <template #cell()="scope">
      <div
        v-if="scope.field.type == 'image'"
        class="text-nowrap"
      >
        <img
          v-if="scope.item[scope.field.key]"
          class="cursor-pointer"
          :width="scope.field.width ? scope.field : `50px`"
          :src="scope.item[scope.field.key]"
          :alt="scope.field.key"
          @click="previewImage(scope.item[scope.field.key])"
        >
      </div>
      <div
        v-else
        class="text-nowrap"
      >
        {{ scope.item[scope.field.key] }}
      </div>
    </template>

    <template
      v-for="(_, slot) of $scopedSlots"
      v-slot:[slot]="scope"
    >
      <slot
        :name="slot"
        v-bind="scope"
      />
    </template>
  </b-table>
</template>

<script>
import {
  BTable,
  BSkeletonTable,
  BLink,
  BButton,
  BBadge,
} from 'bootstrap-vue'
import { $themeConfig } from '@themeConfig'
import JsonViewer from '@/components/JsonViewer.vue'

export default {
  components: {
    BTable,
    BSkeletonTable,
    BLink,
    BButton,
    BBadge,
    JsonViewer,
  },
  props: {
    items: {
      type: Array,
      default() {
        return []
      },
    },
    fields: {
      type: Array,
      default() {
        return []
      },
    },
    primaryField: {
      type: String,
      default: 'name',
    },
    total: {
      type: Number,
      default: 0,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    perPage: {
      type: Number,
      default() {
        return this.$store.state.pagination.perPage
      },
    },
    deletable: {
      type: Boolean,
      default: false,
    },
    updatable: {
      type: Boolean,
      default: true,
    },
    viewModal: {
      type: Boolean,
      default: false,
    },
    archived: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      required: true,
    },
    route: {
      type: String,
      required: false,
    },
    stickyHeader: {
      type: String,
      default: '',
    },
    checkIsDefault: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      item: {},
    }
  },
  methods: {
    getColumnStyle(field) {
      if (field.width) {
        return {
          width: `${field.width}px`,
          minWidth: `${field.width}px`,
          maxWidth: `${field.width}px`,
        }
      }
      return {}
    },
    view(item) {
      this.$emit('view', item)
    },
    previewImage(image) {
      const preview = this.$imagePreview({
        initIndex: 0,
        images: [image],
        zIndex: 9999,
        isEnableDownloadImage: false,
        isEnableImagePageIndicator: false,
        isEnableBlurBackground: true,
        onClose: () => {},
      })
    },
    calculateOrderNumber(index) {
      const from = (this.currentPage - 1) * this.perPage + 1
      return from + index
    },
    submit() {
      this.$emit('submit')
    },
    sortChanged(ctx) {
      const item = this.fields.find(value => {
        if (value.key === ctx.sortBy) {
          return value
        }
      })

      if (!item) {
        return
      }

      const sortBy = item?.sortField ? item.sortField : item?.key
      const sortDirection = ctx.sortDesc ? 'desc' : 'asc'
      this.$emit('sort-changed', sortBy, sortDirection)
    },
    destroy(id, index, data) {
      index = this.calculateOrderNumber(index)
      let name = data[this.primaryField] ?? ''
      if (typeof name === 'object') {
        name = data?.[this.primaryField]?.[this.$i18n.locale.split('-')[0]]
          || data?.[this.primaryField]?.en
          || data?.[this.primaryField]?.zh
          || data?.[this.primaryField]?.km
          || data?.[this.primaryField]?.vi
          || ''
      }
      this.$bvModal
        .msgBoxConfirm(
          this.$t('alert.unableToRevertAfterDelete', {
            item: `#${index} ${name ?? ''}`,
          }),
          {
            title: this.$t('alert.confirmContinueOperation'),
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: this.$t('button.yesDelete'),
            cancelTitle: this.$t('button.cancel'),
            cancelVariant: 'outline-secondary',
            hideHeaderClose: false,
            centered: true,
          },
        )
        .then(value => {
          if (value) {
            this.$emit('destroy', id)
          }
        })
    },
    restore(id, index) {
      index = this.calculateOrderNumber(index)

      this.$bvModal
        .msgBoxConfirm(
          `${this.$t('alert.unableToRevertAfterRestore')} #${index}`,
          {
            title: this.$t('alert.confirmContinueOperation'),
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'success',
            okTitle: this.$t('button.yesRestore'),
            cancelTitle: this.$t('button.cancel'),
            cancelVariant: 'outline-secondary',
            hideHeaderClose: false,
            centered: true,
          },
        )
        .then(value => {
          if (value) {
            this.$emit('restore', id)
          }
        })
    },
  },
  setup() {
    // App Name
    const {
      bordered,
      borderless,
      striped,
      outlined,
      small,
      hover,
      dark,
      fixed,
      footClone,
      noCollapse,
    } = $themeConfig.table
    return {
      bordered,
      borderless,
      striped,
      outlined,
      small,
      hover,
      dark,
      fixed,
      footClone,
      noCollapse,
    }
  },
}
</script>

<style lang="scss">
.list-table {
  position: relative;
  overflow-x: auto;

  td.sticky-column,
  th.sticky-header {
    position: sticky !important;
    background: white !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  td.sticky-column {
    z-index: 1;
    // box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
  }

  th.sticky-header {
    z-index: 2;
    // box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
  }
}
</style>
